import { useEffect, useMemo, useState } from 'react';
import { supabase, Restaurant } from '../lib/supabase';
import { Copy, RefreshCw, Lock } from 'lucide-react';

// Hentes fra Netlify env: VITE_ADMIN_PASS
const ENV_PASS = import.meta.env.VITE_ADMIN_PASS as string | undefined;
const STORAGE_KEY = 'admin_authed_v1';

type Driver = {
  id: string;
  name: string;
  age: number | null;
  phone: string | null;
  vehicle_type: 'bike' | 'moped' | 'car';
  status: 'pending' | 'approved' | 'active' | 'inactive';
};

type Order = {
  id: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string;
  zone: string | null;
  delivery_fee: number;
  subtotal: number;
  total: number;
  status: 'pending' | 'assigned' | 'delivering' | 'completed' | 'cancelled';
  driver_id: string | null;
  created_at: string;
};

type OrderItemRow = {
  id: string;
  order_id: string;
  quantity: number;
  price: number;
  size?: string | null;
  menu_item_id: string;
  menu_items?: { name: string } | null;
};

export default function AdminPage() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [enteredPassword, setEnteredPassword] = useState('');

  // Data state
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItemRow[]>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Generator-valg
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [pickupName, setPickupName] = useState<string>('');
  const [payout, setPayout] = useState<number>(70);
  const [paid, setPaid] = useState<boolean>(true);

  // Hent data når vi er innlogget
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshAll();
  }, [isAuthenticated]);

  async function refreshAll() {
    try {
      setErrorMsg('');
      setLoading(true);

      const [driversRes, ordersRes, restaurantsRes] = await Promise.all([
        supabase.from('drivers')
          .select('*')
          .in('status', ['approved', 'active'])
          .order('name', { ascending: true }),
        supabase.from('orders')
          .select('id, customer_name, customer_phone, customer_address, zone, delivery_fee, subtotal, total, status, driver_id, created_at')
          .order('created_at', { ascending: false })
          .limit(200),
        supabase.from('restaurants')
          .select('id, name, logo_url, description, is_active, created_at')
          .order('created_at', { ascending: false }),
      ]);

      if (driversRes.error) throw driversRes.error;
      if (ordersRes.error) throw ordersRes.error;
      if (restaurantsRes.error) throw restaurantsRes.error;

      const dDrivers = driversRes.data || [];
      const dOrders = ordersRes.data || [];
      const dRestaurants = restaurantsRes.data || [];

      setDrivers(dDrivers);
      setOrders(dOrders);
      setRestaurants(dRestaurants);

      const orderIds = dOrders.map(o => o.id);
      if (orderIds.length) {
        const itemsRes = await supabase
          .from('order_items')
          .select('id, order_id, quantity, price, size, menu_item_id, menu_items(name)')
          .in('order_id', orderIds);
        if (itemsRes.error) throw itemsRes.error;

        const grouped: Record<string, OrderItemRow[]> = {};
        (itemsRes.data || []).forEach(oi => {
          if (!grouped[oi.order_id]) grouped[oi.order_id] = [];
          grouped[oi.order_id].push(oi);
        });
        setItemsByOrder(grouped);
      } else {
        setItemsByOrder({});
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Kunne ikke laste data.');
    } finally {
      setLoading(false);
    }
  }

  // Prefill generator når ordre velges
  useEffect(() => {
    if (!selectedOrderId) return;
    const ord = orders.find(o => o.id === selectedOrderId);
    if (!ord) return;
    const firstName = ord.customer_name?.split(' ')[0] || '';
    setPickupName(firstName);
    setPayout(Math.max(60, Math.round(ord.delivery_fee)));
  }, [selectedOrderId, orders]);

  const selectedOrder = useMemo(
    () => orders.find(o => o.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );
  const selectedDriver = useMemo(
    () => drivers.find(d => d.id === selectedDriverId) || null,
    [drivers, selectedDriverId]
  );
  const selectedItems = useMemo(
    () => (selectedOrder ? itemsByOrder[selectedOrder.id] || [] : []),
    [selectedOrder, itemsByOrder]
  );

  const driverMessage = useMemo(() => {
    if (!selectedOrder || !selectedDriver) return '';
    const lines = selectedItems.map((it, idx) => {
      const n = idx + 1;
      const name = it.menu_items?.name ?? 'Ukjent vare';
      const size = it.size ? ` (${String(it.size).toLowerCase()})` : '';
      const qty = it.quantity > 1 ? `${it.quantity}x ` : '';
      return `${n}. ${qty}${name}${size}`;
    });

    const paidTxt = paid ? 'betalt' : 'ikke betalt';
    const customer = selectedOrder.customer_name;
    const addr = selectedOrder.customer_address;
    const firstNameDriver = selectedDriver.name.split(' ')[0] || selectedDriver.name;

    return `Hei ${firstNameDriver}, ny leveranse! ${customer} - ${addr}.
${lines.join('\n')}
Status: ${paidTxt}, hent under navnet ${pickupName}.
Du tjener: ${Math.round(payout)},- uten mulige tips.`;
  }, [selectedOrder, selectedDriver, selectedItems, pickupName, payout, paid]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(driverMessage);
      alert('Melding kopiert 👍');
    } catch {
      alert('Kunne ikke kopiere. Marker teksten og kopier manuelt.');
    }
  };

  // AUTH GATE (i render, ikke i fetch-funksjoner)
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow text-center max-w-sm w-full">
          <Lock className="w-10 h-10 text-[#004D99] dark:text-orange-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Admin tilgang</h2>
          <input
            type="password"
            value={enteredPassword}
            onChange={(e) => setEnteredPassword(e.target.value)}
            className="w-full px-4 py-3 mb-4 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
            placeholder="Skriv inn passord"
          />
          <button
            onClick={() => {
              const expected = (ENV_PASS || '').trim();
              if (expected && enteredPassword === expected) {
                localStorage.setItem(STORAGE_KEY, 'true');
                setIsAuthenticated(true);
              } else {
                alert('Feil passord');
              }
            }}
            className="w-full py-3 bg-[#004D99] hover:bg-[#003366] text-white rounded-lg font-bold"
          >
            Logg inn
          </button>
          {!ENV_PASS && (
            <p className="mt-3 text-xs text-amber-600">
              VITE_ADMIN_PASS mangler i miljøvariabler.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin</h1>
          <button
            onClick={refreshAll}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Oppdater
          </button>
        </div>

        {errorMsg && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-4 py-3">
            {errorMsg}
          </div>
        )}

        {/* Restauranter */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Restauranter</h2>
            <span className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {restaurants.length} stk
            </span>
          </div>

          {loading && <div className="p-3 text-gray-500">Laster…</div>}

          {!loading && (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 dark:text-gray-300">
                    <th className="py-2 pr-4">Logo</th>
                    <th className="py-2 pr-4">Navn</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Beskrivelse</th>
                    <th className="py-2 pr-4">Opprettet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {restaurants.map(r => (
                    <tr key={r.id} className="text-gray-900 dark:text-gray-100">
                      <td className="py-2 pr-4">
                        {r.logo_url ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={r.logo_url}
                            alt={r.name}
                            className="w-10 h-10 rounded object-contain bg-white"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700" />
                        )}
                      </td>
                      <td className="py-2 pr-4 font-medium">{r.name}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs ${r.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                          {r.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 max-w-[420px] truncate">{r.description || '—'}</td>
                      <td className="py-2 pr-4">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {!restaurants.length && (
                    <tr>
                      <td colSpan={5} className="py-3 text-gray-500">
                        Ingen restauranter funnet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Siste ordrer */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Siste ordrer</h2>
          <div className="max-h-64 overflow-auto divide-y divide-gray-200 dark:divide-gray-700">
            {loading && <div className="p-4 text-gray-500">Laster…</div>}
            {!loading && orders.map(o => (
              <div key={o.id} className="p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {o.customer_name} • {o.customer_address}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(o.created_at).toLocaleString()} • {o.status} • {o.total} kr
                  </div>
                </div>
                <button
                  onClick={() => setSelectedOrderId(o.id)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    selectedOrderId === o.id
                      ? 'bg-[#004D99] text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                  }`}
                >
                  Velg
                </button>
              </div>
            ))}
            {!loading && !orders.length && (
              <div className="p-4 text-gray-500">Ingen ordrer å vise.</div>
            )}
          </div>
        </div>

        {/* Melding til sjåfør */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Melding til sjåfør</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ordre</label>
                <select
                  value={selectedOrderId}
                  onChange={e => setSelectedOrderId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                >
                  <option value="">— Velg ordre —</option>
                  {orders.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.customer_name} • {o.customer_address} • {o.total} kr
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Sjåfør</label>
                <select
                  value={selectedDriverId}
                  onChange={e => setSelectedDriverId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                >
                  <option value="">— Velg sjåfør —</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} {d.phone ? `• ${d.phone}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Hent under navnet</label>
                <input
                  type="text"
                  value={pickupName}
                  onChange={e => setPickupName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                  placeholder="F.eks. Thomas"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={paid} onChange={e => setPaid(e.target.checked)} className="h-4 w-4" />
                  Betalt
                </label>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Utbetaling (kr)</label>
                  <input
                    type="number"
                    min={0}
                    value={payout}
                    onChange={e => setPayout(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Forhåndsvisning</label>
              <textarea
                rows={12}
                readOnly
                value={
                  selectedOrderId && selectedDriverId
                    ? driverMessage
                    : 'Velg både ordre og sjåfør for å generere melding.'
                }
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              />
              <div className="mt-3 flex gap-3">
                <button
                  onClick={copyToClipboard}
                  disabled={!driverMessage}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#004D99] text-white disabled:bg-gray-400"
                >
                  <Copy className="w-4 h-4" />
                  Kopier melding
                </button>
                {selectedDriver?.phone && driverMessage && (
                  <a
                    href={`sms:${encodeURIComponent(selectedDriver.phone)}?&body=${encodeURIComponent(driverMessage)}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  >
                    Åpne i SMS
                  </a>
                )}
              </div>
              {!selectedItems.length && selectedOrderId && (
                <p className="mt-2 text-sm text-amber-600">Ingen ordrelinjer funnet for denne ordren.</p>
              )}
            </div>
          </div>
        </div>

        {/* Sjåfører */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Sjåfører</h2>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 dark:text-gray-300">
                  <th className="py-2 pr-4">Navn</th>
                  <th className="py-2 pr-4">Telefon</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {drivers.map(d => (
                  <tr key={d.id} className="text-gray-900 dark:text-gray-100">
                    <td className="py-2 pr-4">{d.name}</td>
                    <td className="py-2 pr-4">{d.phone || '—'}</td>
                    <td className="py-2 pr-4">{d.vehicle_type}</td>
                    <td className="py-2 pr-4">{d.status}</td>
                  </tr>
                ))}
                {!drivers.length && (
                  <tr>
                    <td colSpan={4} className="py-3 text-gray-500">Ingen sjåfører funnet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
